import { useEffect, useMemo, useRef, useState } from "react";
import { useRoute, useLocation } from "wouter";
import { Avatar } from "@/components/mnf/UI";
import Icon from "@/components/mnf/Icon";
import {
  useStoreQuery,
  useStoreMutation,
  queries,
  mutations,
  tierAtLeast,
  type LiveChatMessage,
} from "@/lib/store";
import { UpsellCard } from "@/components/mnf/TierLock";

/* ----------------------- Floating reactions overlay ----------------------- */
type FloatReaction = { id: number; emoji: string; left: number; duration: number };

/* ---------------------------- Coach cue rotator --------------------------- */
const COACH_CUES = [
  "Inhale through your nose, sister. Long exhale.",
  "Soft knees. Drop the shoulders down your back.",
  "He gives strength to the weary. Press into Him.",
  "Three more. Stay with me. Eyes up.",
];

export default function Live() {
  const [, params] = useRoute<{ id: string }>("/live/:id");
  const [, setLocation] = useLocation();
  const id = params?.id ?? "";

  const cls = useStoreQuery(useMemo(() => () => queries.liveClass(id), [id]));
  const chat = useStoreQuery(useMemo(() => () => queries.liveChatFor(id), [id]));
  const me = useStoreQuery(queries.currentUser);
  const post = useStoreMutation(mutations.postLiveChat);
  const join = useStoreMutation(mutations.joinLiveClass);
  const leave = useStoreMutation(mutations.leaveLiveClass);

  const [draft, setDraft] = useState("");
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Track this sister as a live attendee for the duration of her visit.
  const isLiveStatus = cls?.status === "live";
  useEffect(() => {
    if (!id || !me?.id || !isLiveStatus) return;
    join(id, me.id);
    return () => {
      leave(id, me.id);
    };
  }, [id, me?.id, isLiveStatus, join, leave]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [chat.length]);

  if (!cls) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center">
        <Icon name="videocam_off" size={42} />
        <p className="mnf-serif mt-4" style={{ fontSize: 22, fontWeight: 500, color: "var(--mnf-fd)" }}>
          This class has wrapped
        </p>
        <p className="mnf-body mt-2" style={{ fontSize: 13, color: "var(--mnf-tg)" }}>
          Browse upcoming sessions in Gather.
        </p>
        <button
          onClick={() => setLocation("/gather")}
          className="mnf-body mt-6"
          style={{
            padding: "10px 22px", borderRadius: 999,
            background: "var(--mnf-f)", color: "#F5F0E8",
            fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase",
            border: "none", cursor: "pointer",
          }}
        >
          Back to Gather
        </button>
      </div>
    );
  }

  // Tier gate: if user's tier is below the class minimum, show full-screen upsell
  const isLocked = !tierAtLeast(me?.tier, cls.tierMin);
  if (isLocked) {
    return (
      <div
        className="flex flex-col h-full w-full"
        style={{
          background: "linear-gradient(180deg, #0E1F17 0%, #050B08 100%)",
          color: "#F5F0E8",
        }}
      >
        <div
          className="flex items-center justify-between px-4 py-3 shrink-0"
          style={{ background: "rgba(0,0,0,0.45)", backdropFilter: "blur(10px)" }}
        >
          <button
            onClick={() => setLocation("/gather")}
            data-testid="button-leave-live-locked"
            className="flex items-center justify-center"
            style={{ width: 36, height: 36, borderRadius: 999, background: "rgba(245,240,232,0.1)", color: "#F5F0E8", border: "none", cursor: "pointer" }}
            aria-label="Back"
          >
            <Icon name="arrow_back_ios_new" size={16} />
          </button>
          <p className="mnf-eyebrow" style={{ color: "rgba(245,240,232,0.7)", fontSize: 9.5 }}>
            Locked
          </p>
          <span style={{ width: 36 }} />
        </div>
        <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
          <Avatar initials="K" size={72} ringColor="rgba(245,240,232,0.4)" />
          <p className="mnf-serif italic mt-4" style={{ fontSize: 22, color: "#F5F0E8", lineHeight: 1.2 }}>
            {cls.title}
          </p>
          <p className="mnf-body italic mt-2" style={{ fontSize: 13, color: "rgba(245,240,232,0.7)", maxWidth: 300 }}>
            This circle is held for {cls.tierMin} sisters.
          </p>
          <div className="w-full mt-6" style={{ maxWidth: 360 }}>
            <UpsellCard
              tier={cls.tierMin}
              eyebrow="Live with Coach"
              title={`Bloom into ${cls.tierMin}`}
              description={`This ${cls.durationMin}-minute session is part of the ${cls.tierMin} circle. Step up a tier to join Coach Kiley live and access the full circle.`}
              ctaLabel="See tiers"
              onCta={() => setLocation("/garden")}
              icon="videocam"
            />
          </div>
        </div>
      </div>
    );
  }

  const isLive = cls.status === "live";
  const isReplay = cls.status === "replay";
  const isUpcoming = cls.status === "upcoming";

  // Rotating coach cue caption (live only)
  const [cueIdx, setCueIdx] = useState(0);
  useEffect(() => {
    if (!isLive) return;
    const t = setInterval(() => setCueIdx((i) => (i + 1) % COACH_CUES.length), 5500);
    return () => clearInterval(t);
  }, [isLive]);

  // Floating reactions on stage
  const [floats, setFloats] = useState<FloatReaction[]>([]);
  const floatId = useRef(0);
  const burstReaction = (emoji: string) => {
    const burst: FloatReaction[] = Array.from({ length: 3 }).map(() => ({
      id: ++floatId.current,
      emoji,
      left: 20 + Math.random() * 60,
      duration: 1700 + Math.random() * 800,
    }));
    setFloats((f) => [...f, ...burst]);
    burst.forEach((b) => {
      setTimeout(() => setFloats((f) => f.filter((x) => x.id !== b.id)), b.duration + 50);
    });
  };

  const send = (type: "chat" | "prayer" | "amen", body: string, emoji?: string) => {
    if (!body.trim() && type === "chat") return;
    post({
      classId: id,
      userId: me?.id ?? "u_guest",
      userName: me?.name ?? "You",
      initials: (me?.name ?? "Y").slice(0, 1).toUpperCase(),
      body: body || (type === "prayer" ? "🙏 Praying" : "🙌 Amen"),
      type,
    });
    if (type === "chat") setDraft("");
    if (emoji) burstReaction(emoji);
  };

  // Pseudo presence list from chat (recent unique participants)
  const presence = useMemo(() => {
    const seen = new Map<string, { initials: string; userName: string }>();
    for (let i = chat.length - 1; i >= 0 && seen.size < 6; i--) {
      const m = chat[i];
      if (!seen.has(m.userId)) seen.set(m.userId, { initials: m.initials, userName: m.userName });
    }
    return Array.from(seen.values());
  }, [chat]);

  const watching = isLive ? cls.watching : cls.attendees;

  return (
    <div
      className="flex flex-col h-full w-full"
      style={{
        background: "linear-gradient(180deg, #0E1F17 0%, #050B08 100%)",
        color: "#F5F0E8",
      }}
    >
      {/* Top bar */}
      <div
        className="flex items-center justify-between px-4 py-3 shrink-0"
        style={{ background: "rgba(0,0,0,0.45)", backdropFilter: "blur(10px)" }}
      >
        <button
          onClick={() => setLocation("/gather")}
          data-testid="button-leave-live"
          className="flex items-center justify-center"
          style={{ width: 36, height: 36, borderRadius: 999, background: "rgba(245,240,232,0.1)", color: "#F5F0E8", border: "none", cursor: "pointer" }}
          aria-label="Leave"
        >
          <Icon name="arrow_back_ios_new" size={16} />
        </button>
        <div className="text-center flex-1 mx-3 min-w-0">
          <p
            className="mnf-eyebrow"
            style={{
              color: isLive ? "#FF8585" : "rgba(245,240,232,0.7)",
              fontSize: 9.5,
            }}
          >
            {isLive ? "● Live" : isReplay ? "Replay" : "Starting soon"}
          </p>
          <p className="mnf-serif italic truncate" style={{ fontSize: 15 }}>
            {cls.title}
          </p>
        </div>
        <div
          className="flex items-center gap-1.5 mnf-body"
          style={{
            padding: "6px 11px", borderRadius: 999,
            background: "rgba(245,240,232,0.1)", fontSize: 11, fontWeight: 600,
          }}
        >
          <Icon name="visibility" size={13} />
          {isLive ? cls.watching : cls.attendees}
        </div>
      </div>

      {/* Video stage (16:9) */}
      <div
        className="relative shrink-0"
        style={{
          aspectRatio: "16/9",
          background:
            "radial-gradient(ellipse at 30% 20%, rgba(192,120,74,0.35) 0%, transparent 60%)," +
            "radial-gradient(ellipse at 80% 80%, rgba(168,212,184,0.18) 0%, transparent 55%)," +
            "linear-gradient(165deg, #1E4A35 0%, #050B08 100%)",
          overflow: "hidden",
        }}
      >
        {/* Coach avatar centered "broadcast" */}
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
          <Avatar initials="K" size={92} ringColor="rgba(245,240,232,0.6)" />
          <p className="mnf-serif italic mt-4" style={{ fontSize: 22, color: "#F5F0E8", lineHeight: 1.2 }}>
            Coach Kiley
          </p>
          <p className="mnf-body italic mt-1" style={{ fontSize: 12, color: "rgba(245,240,232,0.7)" }}>
            {isLive ? "Broadcasting live" : isReplay ? "Replay" : "Stream begins shortly"}
          </p>
        </div>

        {/* Live indicator overlay */}
        {isLive && (
          <div
            className="absolute top-3 left-3 flex items-center gap-1.5 mnf-body"
            style={{
              padding: "5px 10px", borderRadius: 999,
              background: "rgba(255,90,90,0.85)", color: "#fff",
              fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase",
            }}
          >
            <span style={{ width: 6, height: 6, borderRadius: 999, background: "#fff" }} />
            Live
          </div>
        )}

        {/* Tier overlay */}
        <div
          className="absolute bottom-3 left-3 mnf-body"
          style={{
            padding: "4px 10px", borderRadius: 999,
            background: "rgba(0,0,0,0.45)", color: "#F5F0E8",
            fontSize: 10, fontWeight: 600, letterSpacing: "0.04em",
          }}
        >
          {cls.tierMin}+ · {cls.durationMin}m
        </div>

        {/* Verse pinned */}
        {cls.verseRef && (
          <div
            className="absolute bottom-3 right-3 mnf-body italic"
            style={{
              padding: "4px 10px", borderRadius: 999,
              background: "rgba(127,119,221,0.35)", color: "#F5F0E8",
              fontSize: 10, fontWeight: 500,
            }}
          >
            {cls.verseRef}
          </div>
        )}

        {/* Floating reactions */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {floats.map((f) => (
            <span
              key={f.id}
              className="absolute mnf-float-reaction"
              style={{
                left: `${f.left}%`,
                bottom: 4,
                fontSize: 26,
                animationDuration: `${f.duration}ms`,
              }}
            >
              {f.emoji}
            </span>
          ))}
        </div>
      </div>

      {/* Coach cue + sister presence strip */}
      {(isLive || isReplay) && (
        <div
          className="px-4 py-3 shrink-0 flex items-center gap-3"
          style={{ background: "rgba(245,240,232,0.04)", borderBottom: "1px solid rgba(245,240,232,0.06)" }}
        >
          <div className="flex-1 min-w-0">
            <p className="mnf-eyebrow" style={{ color: "rgba(229,185,151,0.95)", fontSize: 9 }}>
              {isLive ? "Coach is saying" : "Replay highlight"}
            </p>
            <p
              key={cueIdx}
              className="mnf-serif italic mnf-cue-fade"
              style={{ fontSize: 13, color: "#F5F0E8", lineHeight: 1.35, marginTop: 2 }}
            >
              {COACH_CUES[cueIdx]}
            </p>
          </div>
          {presence.length > 0 && (
            <div className="flex items-center shrink-0" style={{ paddingLeft: 4 }}>
              <div className="flex">
                {presence.slice(0, 3).map((p, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-center mnf-body"
                    style={{
                      width: 26, height: 26, borderRadius: 999,
                      background: "rgba(245,240,232,0.18)",
                      color: "#F5F0E8", fontSize: 10, fontWeight: 700,
                      border: "2px solid #050B08",
                      marginLeft: i === 0 ? 0 : -8,
                    }}
                    aria-label={p.userName}
                    title={p.userName}
                  >
                    {p.initials}
                  </div>
                ))}
              </div>
              <p className="mnf-body" style={{ fontSize: 10, color: "rgba(245,240,232,0.7)", marginLeft: 8 }}>
                +{Math.max(0, watching - 3)}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Chat */}
      <div className="flex-1 flex flex-col min-h-0">
        <div
          className="px-4 py-2 shrink-0 flex items-center justify-between"
          style={{ borderBottom: "1px solid rgba(245,240,232,0.08)" }}
        >
          <p className="mnf-eyebrow" style={{ color: "rgba(245,240,232,0.7)", fontSize: 9.5 }}>
            Live circle
          </p>
          <p className="mnf-body" style={{ fontSize: 10.5, color: "rgba(245,240,232,0.5)" }}>
            {chat.length} sisters chatting
          </p>
        </div>

        {/* Pinned coach prompt */}
        {isLive && cls.verseRef && (
          <div
            className="mx-4 mt-3 mb-1 px-3 py-2 flex items-center gap-2 shrink-0"
            style={{
              background: "rgba(127,119,221,0.18)",
              border: "1px solid rgba(127,119,221,0.35)",
              borderRadius: 14,
            }}
          >
            <Icon name="push_pin" size={14} />
            <p className="mnf-body italic" style={{ fontSize: 11.5, color: "rgba(245,240,232,0.85)", lineHeight: 1.35 }}>
              <span style={{ fontWeight: 700, fontStyle: "normal" }}>{cls.verseRef}</span>
              <span style={{ opacity: 0.75 }}> · drop a prayer or a praise as we lift together.</span>
            </p>
          </div>
        )}

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
          {chat.length === 0 ? (
            <p className="mnf-body italic text-center" style={{ fontSize: 12, color: "rgba(245,240,232,0.5)", paddingTop: 24 }}>
              Be the first to greet your sisters.
            </p>
          ) : (
            chat.map((m) => <ChatBubble key={m.id} m={m} />)
          )}
        </div>

        {/* Reactions + composer */}
        {(isLive || isUpcoming) && (
          <div
            className="px-3 py-3 shrink-0 space-y-2"
            style={{ background: "rgba(0,0,0,0.35)", borderTop: "1px solid rgba(245,240,232,0.06)" }}
          >
            <div className="flex items-center gap-2">
              <ReactionBtn label="🙏 Praying" onClick={() => send("prayer", "🙏 Praying for you, sister", "🙏")} testid="button-react-prayer" />
              <ReactionBtn label="🙌 Amen" onClick={() => send("amen", "🙌 Amen", "🙌")} testid="button-react-amen" />
              <ReactionBtn label="💛 Lifting" onClick={() => send("amen", "💛 Lifting you up", "💛")} testid="button-react-lifting" />
            </div>
            <div className="flex items-center gap-2">
              <input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") send("chat", draft); }}
                data-testid="input-live-chat"
                placeholder="Speak grace into the circle…"
                className="flex-1 mnf-body italic outline-none"
                style={{
                  padding: "10px 14px", borderRadius: 999,
                  background: "rgba(245,240,232,0.10)", color: "#F5F0E8",
                  fontSize: 13, border: "1px solid rgba(245,240,232,0.12)",
                }}
              />
              <button
                onClick={() => send("chat", draft)}
                data-testid="button-send-chat"
                className="flex items-center justify-center shrink-0"
                style={{
                  width: 40, height: 40, borderRadius: 999,
                  background: "var(--mnf-t)", color: "#fff",
                  border: "none", cursor: "pointer",
                }}
                aria-label="Send"
              >
                <Icon name="send" size={16} weight={500} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ChatBubble({ m }: { m: LiveChatMessage }) {
  const isReact = m.type === "prayer" || m.type === "amen";
  const tone =
    m.type === "prayer"
      ? "rgba(127,119,221,0.20)"
      : m.type === "amen"
      ? "rgba(160,120,32,0.20)"
      : "rgba(245,240,232,0.08)";
  return (
    <div className="flex items-start gap-2">
      <Avatar initials={m.initials} size={26} />
      <div className="flex-1 min-w-0">
        <p className="mnf-body" style={{ fontSize: 11, fontWeight: 700, color: "rgba(245,240,232,0.85)" }}>
          {m.userName}
        </p>
        <div
          className="inline-block mnf-body"
          style={{
            marginTop: 2, padding: "6px 11px", borderRadius: 14,
            background: tone, color: "#F5F0E8", fontSize: 12.5, lineHeight: 1.45,
            fontStyle: isReact ? "italic" : undefined,
            maxWidth: "90%", wordBreak: "break-word",
          }}
        >
          {m.body}
        </div>
      </div>
    </div>
  );
}

function ReactionBtn({ label, onClick, testid }: { label: string; onClick: () => void; testid: string }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="mnf-body transition-all hover-elevate"
      style={{
        padding: "7px 12px", borderRadius: 999,
        background: "rgba(245,240,232,0.10)", color: "#F5F0E8",
        fontSize: 11.5, fontWeight: 600,
        border: "1px solid rgba(245,240,232,0.12)", cursor: "pointer",
      }}
    >
      {label}
    </button>
  );
}
